<template>
  <v-main>
    <v-container fluid>
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              title="Admin Management"
              subtitle="Manage admin accounts for this platform"
            >
              <template v-slot:actions>
                <div class="d-flex justify-end align-center">
                    <v-btn color="primary" elevation="1" small to="/users/create-admin" class="px-3">
                      <v-icon left x-small>mdi-plus</v-icon>
                      Create Admin
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <v-card-subtitle class="pb-2 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="5" class="pa-0">
                  <v-text-field
                    v-model="search"
                    placeholder="Search by name, email or phone..."
                    prepend-inner-icon="mdi-magnify"
                    outlined dense hide-details clearable
                    @input="onSearch"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-subtitle>
          </v-card>
        </v-col>
      </v-row>

      <!-- Table -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col class="pa-0">
                  <v-chip x-small color="primary" class="mr-2">{{ totalItems }} Admins</v-chip>
                </v-col>
                <v-col cols="auto" class="pa-0">
                  <span class="text-caption grey--text">Show:</span>
                  <v-btn-toggle v-model="rowsPerPage" mandatory dense class="ml-2" @change="getData">
                    <v-btn x-small :value="10">10</v-btn>
                    <v-btn x-small :value="25">25</v-btn>
                    <v-btn x-small :value="50">50</v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>
            </v-card-title>

            <v-data-table
              :headers="headers"
              :items="admins"
              :loading="loading"
              :items-per-page="rowsPerPage"
              hide-default-footer
              class="admin-table"
            >
              <template #[`item.sr`]="{ index }">
                <span class="text-caption grey--text">{{ (currentPage - 1) * rowsPerPage + index + 1 }}</span>
              </template>

              <template #[`item.name`]="{ item }">
                <div class="d-flex align-center py-2">
                  <v-avatar size="34" color="primary" class="mr-3">
                    <span class="white--text text-caption font-weight-bold">{{ initials(item) }}</span>
                  </v-avatar>
                  <div>
                    <div class="text-body-2 font-weight-medium">{{ fullName(item) }}</div>
                    <div class="text-caption grey--text">{{ item.email }}</div>
                  </div>
                </div>
              </template>

              <template #[`item.phone`]="{ item }">
                <span class="text-body-2">{{ item.phone || '—' }}</span>
              </template>

              <template #[`item.is_active`]="{ item }">
                <v-chip x-small :color="item.is_active ? 'success' : 'error'" text-color="white">
                  {{ item.is_active ? 'Active' : 'Inactive' }}
                </v-chip>
              </template>

              <template #[`item.created_at`]="{ item }">
                <span class="text-caption grey--text">{{ formatDate(item.created_at) }}</span>
              </template>

              <!-- Actions -->
              <template #[`item.actions`]="{ item }">
                <div class="d-flex align-center" style="gap:6px">
                  <v-btn icon small color="primary" @click="openEdit(item)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon small color="error" @click="openDelete(item)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </template>

              <template #no-data>
                <div class="text-center py-10 grey--text">
                  <v-icon size="48" color="grey lighten-2">mdi-shield-account-outline</v-icon>
                  <div class="mt-2">No admin accounts found</div>
                </div>
              </template>
            </v-data-table>

            <v-divider></v-divider>
            <div class="d-flex align-center justify-space-between px-4 py-2">
              <span class="text-caption grey--text">
                {{ (currentPage - 1) * rowsPerPage + 1 }}–{{ Math.min(currentPage * rowsPerPage, totalItems) }} of {{ totalItems }}
              </span>
              <v-pagination
                v-model="currentPage"
                :length="totalPages"
                :total-visible="5"
                dense
                @input="getData"
              ></v-pagination>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Edit Dialog -->
      <v-dialog v-model="editDialog" max-width="520" persistent>
        <v-card>
          <v-card-title class="primary white--text py-3">
            <v-icon left color="white">mdi-pencil</v-icon>
            Edit Admin
          </v-card-title>
          <v-card-text class="pt-4">
            <v-alert v-if="editError" type="error" dense class="mb-3">{{ editError }}</v-alert>
            <v-row dense>
              <v-col cols="6">
                <v-text-field v-model="editForm.first_name" label="First Name *" outlined dense
                  :error-messages="editErrors.first_name" hide-details="auto" />
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="editForm.last_name" label="Last Name *" outlined dense
                  :error-messages="editErrors.last_name" hide-details="auto" />
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="editForm.email" label="Email *" outlined dense
                  :error-messages="editErrors.email" hide-details="auto" />
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="editForm.phone" label="Phone" outlined dense maxlength="10"
                  :error-messages="editErrors.phone" hide-details="auto" />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editForm.password"
                  label="New Password (leave blank to keep current)"
                  outlined dense
                  :type="showPassword ? 'text' : 'password'"
                  :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="showPassword = !showPassword"
                  :error-messages="editErrors.password"
                  hide-details="auto"
                  autocomplete="new-password"
                />
              </v-col>
              <v-col cols="12">
                <v-select v-model="editForm.is_active" label="Status" outlined dense
                  :items="[{text:'Active',value:true},{text:'Inactive',value:false}]"
                  hide-details="auto" />
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider />
          <v-card-actions class="px-4 py-3">
            <v-spacer />
            <v-btn text @click="editDialog = false">Cancel</v-btn>
            <v-btn color="primary" :loading="editLoading" @click="saveEdit">
              <v-icon left small>mdi-check</v-icon> Save Changes
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="420">
        <v-card>
          <v-card-title class="error white--text py-3">
            <v-icon left color="white">mdi-delete</v-icon>
            Delete Admin
          </v-card-title>
          <v-card-text class="pt-4">
            <p class="text-body-2">
              Are you sure you want to delete
              <strong>{{ deleteTarget ? fullName(deleteTarget) : '' }}</strong>?
              This action can be undone from the Trash.
            </p>
          </v-card-text>
          <v-divider />
          <v-card-actions class="px-4 py-3">
            <v-spacer />
            <v-btn text @click="deleteDialog = false">Cancel</v-btn>
            <v-btn color="error" :loading="deleteLoading" @click="confirmDelete">
              <v-icon left small>mdi-delete</v-icon> Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from '@/axios';

let searchTimer = null;

export default {
  name: 'AdminListPage',
  data() {
    return {
      loading: false,
      admins: [],
      totalItems: 0,
      currentPage: 1,
      rowsPerPage: 10,
      search: '',
      headers: [
        { text: '#',        value: 'sr',         sortable: false, width: '60px' },
        { text: 'Name',     value: 'name',        sortable: false },
        { text: 'Phone',    value: 'phone',       sortable: false, width: '140px' },
        { text: 'Status',   value: 'is_active',   sortable: false, width: '100px' },
        { text: 'Created',  value: 'created_at',  sortable: false, width: '130px' },
        { text: 'Actions',  value: 'actions',     sortable: false, width: '90px',  align: 'center' },
      ],

      // Edit
      editDialog: false,
      editLoading: false,
      editError: '',
      editErrors: {},
      editTarget: null,
      editForm: { first_name: '', last_name: '', email: '', phone: '', is_active: true, password: '' },
      showPassword: false,

      // Delete
      deleteDialog: false,
      deleteLoading: false,
      deleteTarget: null,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.rowsPerPage) || 1;
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.loading = true;
      try {
        const res = await axios.get('/admins', {
          params: { search_keyword: this.search || undefined, rowsPerPage: this.rowsPerPage, page: this.currentPage },
        });
        const data = res.data?.data;
        this.admins     = data?.data  || [];
        this.totalItems = data?.total || 0;
      } catch {
        this.$toast?.error('Failed to load admins');
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => { this.currentPage = 1; this.getData(); }, 350);
    },

    // ── Edit ────────────────────────────────────────────
    openEdit(item) {
      this.editTarget  = item;
      this.editError   = '';
      this.editErrors  = {};
      this.editForm    = {
        first_name: item.first_name || '',
        last_name:  item.last_name  || '',
        email:      item.email      || '',
        phone:      item.phone      || '',
        is_active:  !!item.is_active,
        password:   '',
      };
      this.showPassword = false;
      this.editDialog  = true;
    },
    async saveEdit() {
      this.editError  = '';
      this.editErrors = {};
      this.editLoading = true;
      try {
        const payload = { ...this.editForm };
        if (!payload.password) delete payload.password;
        await axios.patch(`/users/${this.encryptIt(String(this.editTarget.id))}`, payload);
        this.$toast?.success('Admin updated successfully.');
        this.editDialog = false;
        this.getData();
      } catch (e) {
        const d = e.response?.data;
        if (d?.errors)  this.editErrors = d.errors;
        else            this.editError  = d?.message || 'Failed to update admin.';
      } finally {
        this.editLoading = false;
      }
    },

    // ── Delete ──────────────────────────────────────────
    openDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      this.deleteLoading = true;
      try {
        await axios.post(`/users/delete/${this.encryptIt(String(this.deleteTarget.id))}`);
        this.$toast?.success('Admin deleted successfully.');
        this.deleteDialog = false;
        this.getData();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to delete admin.');
      } finally {
        this.deleteLoading = false;
      }
    },

    // ── Helpers ─────────────────────────────────────────
    fullName(u) {
      return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.user_name || '—';
    },
    initials(u) {
      return this.fullName(u).split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
    },
    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
.admin-table >>> .v-data-table__wrapper table { border-collapse: collapse; }
.admin-table >>> tbody tr:hover { background-color: rgba(25, 118, 210, 0.04) !important; }
</style>
